import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Paperclip, LoaderCircle } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { PutObjectCommand } from "@aws-sdk/client-s3"; 
import { ModalRemoveDocReact } from '../../modals/ModalRemoveDocReact.js';
import { createS3Client } from "../../credentials/awsClients.js";
import InternetStatus from '../../components/conexao.tsx';
import { CountdownBar } from '../../components/CountdownBar.tsx';
import HeaderHora from '../../components/headerHora.tsx';

const s3Client = createS3Client();

export function UploadDocsIDD() {
  const [docsMeta, setDocsMeta] = useState([]); 

  const [documents, setDocuments] = useState({});

  const [isOpen, setIsOpen] = useState(false);
  const [docToRemove, setDocToRemove] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const inputRefs = useRef({});

  const navigate = useNavigate();

  useEffect(() => {
    const raw = sessionStorage.getItem('dadosLinkAtual');
    if (!raw) return;

    const info = JSON.parse(raw);

    let docs = [];
    try {
      docs = info.outDocsCaptura ? JSON.parse(info.outDocsCaptura) : [];

      try {
        const hasDocsPendentes = Array.isArray(docs) && docs.some(d => d.recebido === 'N');

        if(!hasDocsPendentes){
          navigate('/face')
        }

      } catch (e) {
        console.error('Erro ao fazer parse de outDocsCaptura', e);
      }
    } catch (e) {
      console.error('Erro ao fazer parse de outDocsCaptura:', e);
    }

    setDocsMeta(docs);

    const initialDocsState = {};
    docs.forEach(d => {
      initialDocsState[d.codigo] = {
        file: null,
        status: d.recebido === 'S' ? 'success' : 'idle',
        error: null
      };
    });
    setDocuments(initialDocsState);
  }, [navigate]);

  const handleFileSelect = (event, codigoDoc) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const file = files[0];

    if (file.type !== 'application/pdf') {
      alert('Envie apenas arquivos em PDF.');
      event.target.value = "";
      return;
    }

    const sizeInMB = file.size / 1024 / 1024;
    if (sizeInMB > 5) {
      alert('O arquivo deve ter no máximo 5MB.');
      event.target.value = "";
      return;
    }

    setDocuments(prev => ({
      ...prev,
      [codigoDoc]: { file, status: 'pending', error: null }
    }));
  };

  const handleRemoveFile = () => {
    if (!docToRemove) {
      setIsOpen(false);
      return;
    }

    setDocuments(prev => ({
      ...prev,
      [docToRemove]: { file: null, status: 'idle', error: null }
    }));

    if (inputRefs.current[docToRemove]) {
      inputRefs.current[docToRemove].value = "";
    }

    setDocToRemove(null);
    setIsOpen(false);
  };

  const handleNavigate = useCallback(async () => {
    if (!docsMeta.length) {
      alert('Nenhum documento configurado para este serviço.');
      return;
    }

    const faltando = docsMeta.filter(d => {
      const st = documents[d.codigo];
      return !st || (st.status !== 'success' && !st.file);
    });

    if (faltando.length > 0) {
      alert('Envie todos os documentos em PDF antes de continuar.');
      return;
    }

    setIsLoading(true);

    const raw = sessionStorage.getItem('dadosLinkAtual');
    const info = raw ? JSON.parse(raw) : {};
    const basePath = info.outDocumento;

    const updatedDocs = { ...documents };
    let updatedMeta = [...docsMeta];

    for (const docMeta of docsMeta) {
      const id = docMeta.codigo;
      const docState = documents[id];

      if (!docState?.file || docState.status === 'success') continue;

      const file = docState.file;

      try {
        const ext = file.name?.split('.').pop().toLowerCase() || 'pdf';
        const fileName = `${crypto.randomUUID()}.${ext}`;
        const key = `${basePath}/docs/${fileName}`;

        const params = {
          Bucket: process.env.REACT_APP_AWS_BUCKET,
          Key: key,
          Body: file,
          ContentType: file.type,
        };

        const putFile = await s3Client.send(new PutObjectCommand(params));

        if (putFile.$metadata.httpStatusCode !== 200) {
          throw new Error('Erro ao enviar arquivo para o S3.');
        }

        const res = await fetch(`${process.env.REACT_APP_URL_API}/conform/send-file`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('sessionTokenBearer')}`,
            'fileName': key,
            'token': sessionStorage.getItem('sessionToken'),
            'tipo': id
          }
        });

        if (!res.ok) {
          throw new Error('Erro ao comunicar com a API.');
        }

        const data = await res.json();

        if (data.erros === false) {
          updatedDocs[id] = { file, status: 'success', error: null };
          updatedMeta = updatedMeta.map(d =>
            d.codigo === id ? { ...d, recebido: 'S' } : d
          );
        } else {
          throw new Error(data.mensagem || 'Erro ao registrar o documento.');
        }
      } catch (error) {
        console.error(`Erro ao enviar o documento ${id}:`, error);
        updatedDocs[id] = {
          file: docState.file,
          status: 'error',
          error: error?.message || 'Erro ao enviar este documento. Tente novamente.'
        };
      }
    }

    setDocuments(updatedDocs);
    setDocsMeta(updatedMeta);

    const raw2 = sessionStorage.getItem('dadosLinkAtual');
    const info2 = raw2 ? JSON.parse(raw2) : {};
    info2.outDocsCaptura = JSON.stringify(updatedMeta);

    const allReceived = updatedMeta.every(d => d.recebido === 'S');

    if (allReceived) {
      sessionStorage.setItem('dadosLinkAtual', JSON.stringify(info2));
      navigate('/face');
    } else {
      sessionStorage.setItem('dadosLinkAtual', JSON.stringify(info2));
      alert('Alguns documentos não foram enviados. Verifique os que estão com erro e tente novamente apenas eles.');
    }

    setIsLoading(false);
  }, [docsMeta, documents, navigate]);

  const isContinueDisabled =
    isLoading ||
    docsMeta.some(d => {
      const st = documents[d.codigo];
      return !st || (st.status !== 'success' && !st.file);
    });

  return (
    <div className='content'>
      <div className='box-document'>
        <HeaderHora/>
        <div className='status-face'>
            <CountdownBar/>
            <InternetStatus/>
        </div>
        {docsMeta
        .filter(doc => doc.recebido === 'N')
        .map(doc => {
          const state = documents[doc.codigo] || {};
          const hasFile = !!state.file;
          const sizeMB = state.file ? Math.round(state.file.size / 1024 / 1024) : null;

          return (
            <div className='document' key={doc.codigo}>
              <div className='l'>
                <div className='title'>{doc.documento}</div>
                <div className='subtitle'>
                  Envie o documento apenas em formato PDF (até 5MB).
                </div>

                {(hasFile || state.status === 'success' || state.status === 'error') && (
                  <div
                    className={
                      `doc-uploaded` +
                      (state.status === 'error' ? ' doc-error' : '') +
                      (state.status === 'success' ? ' doc-success' : '')
                    }
                  >
                    {hasFile && <Paperclip size={14} />}
                    {hasFile && (
                      <strong>
                        {state.file.name} ({sizeMB}MB)
                      </strong>
                    )}

                    {state.status === 'pending' && (
                      <span className='doc-status'> (aguardando envio)</span>
                    )}
                    {state.status === 'success' && (
                      <span className='doc-status'> (enviado)</span>
                    )}
                    {state.status === 'error' && (
                      <span className='doc-status'>
                        {' '} - Erro ao enviar, tente novamente.
                      </span>
                    )}

                    {hasFile && (
                      <span
                        onClick={() => {
                          setDocToRemove(doc.codigo);
                          setIsOpen(true);
                        }}
                      >
                        [ Excluir ]
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div
                className='r'
                onClick={() => inputRefs.current[doc.codigo]?.click()}
              >
                <Upload size={20} />
                <input
                  type="file"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  ref={el => (inputRefs.current[doc.codigo] = el)}
                  onChange={(e) => handleFileSelect(e, doc.codigo)}
                />
              </div>
            </div>
          );
        })}

        <button onClick={handleNavigate} disabled={isContinueDisabled}>
          {isLoading
            ? <LoaderCircle className="spin-loader" size={18} />
            : 'CONTINUAR'
          }
        </button>

        <ModalRemoveDocReact
          isOpen={isOpen}
          onRequestClose={() => {
            setIsOpen(false);
            setDocToRemove(null);
          }}
        >
          <div className='text-4'>Deseja excluir este arquivo?</div>
          <div className='text-5'>Ele será apagado permanentemente do sistema.</div>

          <div className='buttons-group'>
            <button
              onClick={() => {
                setIsOpen(false);
                setDocToRemove(null);
              }}
              className='button-secondary'
            >
              CANCELAR
            </button>
            <button onClick={handleRemoveFile}>CONFIRMAR</button>
          </div>
        </ModalRemoveDocReact>

      </div>
    </div>
  );
}
